import React, { useContext } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Popper, PopperProps, Typography } from '@mui/material';
import getNewRequestParams from '@utils/getNewRequestParams';
import Select from '../../../../components/Select';
import { useAvContext } from '../../../../context/AvContextProvider';
import { FeatureFlags } from '../../../../types';
import { SortedTimeBucketOptions } from '../../../Reports/types';
import { CustomDashboardContext } from '../../CustomDashboardContext';
import { AllTypeWidgets } from '../../types';
import { FieldType } from '../../types/types';
import { shouldDisableField } from '../Tabs/MetricDimensionSelect/utils';
import { hasTimeBucket } from './utils';

const TooltipPopper = (props: PopperProps) => (
  <Popper
    {...props}
    sx={theme => ({
      '& .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-disabled="true"]': {
        pointerEvents: 'auto',
        opacity: 1,
      },
      '& .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-disabled="true"] .MuiCheckbox-root': {
        color: theme.palette.action.disabled,
      },
    })}
  />
);

const TimeBucketBreakdown = ({ isMultiBreakdown = false }: { isMultiBreakdown?: boolean }) => {
  const { featureFlags } = useAvContext();
  const { newWidget, setNewWidget } = useContext(CustomDashboardContext);

  const singleOnChange = (value: string) => {
    const { newSelect, newGroupBy, newSortBy, newTop } = getNewRequestParams({
      fields: [value],
      fieldCategory: FieldType.Dimensions,
      widget: newWidget,
      isHistoricalAnalyticsFeatureFlag: featureFlags[FeatureFlags.HistoricalAnalytics],
      isMultiTimeBreakdown: false,
    });

    const newDims = newSelect.dims.filter(({ name }) => !(hasTimeBucket(name) && name !== value));
    const filteredGroupBy = newGroupBy.filter(groupBy => !(hasTimeBucket(groupBy) && groupBy !== value));

    setNewWidget({
      ...newWidget,
      requests: [
        {
          ...newWidget.requests[0],
          select: {
            ...newSelect,
            dims: newDims,
          },
          groupBy: filteredGroupBy,
          sorting: newSortBy,
          top: newTop,
        },
      ],
    } as AllTypeWidgets);
  };
  const multiOnChange = (values: string[], updatedSelectedOption, eventName, selectedOption) => {
    if (
      updatedSelectedOption.length &&
      shouldDisableField({
        newWidget,
        field: { id: selectedOption.value, value: selectedOption.value },
        fieldType: [] as any,
        featureFlags,
      }).disabled
    ) {
      return;
    }

    const dimsSet = new Set(newWidget.requests[0].select.dims.map(({ name }) => name));
    const valuesSet = new Set(values);

    const currentlySelectedValue = ((values as string[]).find(val => !dimsSet.has(val)) ||
      newWidget.requests[0].select.dims.find(({ name }) => !valuesSet.has(name))?.name)!;

    const { newSelect, newGroupBy, newSortBy, newTop, newSortingCol } = getNewRequestParams({
      fields: [selectedOption ? selectedOption.value : currentlySelectedValue],
      fieldCategory: FieldType.Dimensions,
      widget: newWidget,
      isHistoricalAnalyticsFeatureFlag: featureFlags[FeatureFlags.HistoricalAnalytics],
    });

    setNewWidget({
      ...newWidget,
      requests: [
        {
          ...newWidget.requests[0],
          select: newSelect,
          groupBy: newGroupBy,
          sorting: newSortBy,
          top: newTop,
          ...(newSortingCol ? { sortingCol: newSortingCol } : {}),
        },
      ],
    } as AllTypeWidgets);
  };
  return isMultiBreakdown ? (
    <Box sx={{ ...flex.col, gap: '2px' }}>
      <Box sx={{ fontSize: 12, color: theme => theme.palette.colors.neutrals[550] }}>Break down by</Box>
      <Select
        size="xSmall"
        value={newWidget.requests[0].select.dims.filter(({ name }) => hasTimeBucket(name)).map(({ name }) => name)}
        getValueFunc={({ value }: any) => value}
        getLabelFunc={({ label }: any) => label}
        onChange={multiOnChange}
        options={SortedTimeBucketOptions}
        isMultiple
        muiProps={{
          getOptionDisabled: option =>
            shouldDisableField({ newWidget, field: { id: option.value, value: option.value }, fieldType: [] as any, featureFlags })
              .disabled,
          PopperComponent: TooltipPopper,
        }}
        renderOption={option => {
          const isDisabled = shouldDisableField({
            newWidget,
            field: { id: option.value, value: option.value },
            fieldType: [] as any,
            featureFlags,
          });
          const label = <Typography variant="body2">{option.label}</Typography>;

          if (isDisabled.disabled) {
            return <AvTooltip title={isDisabled.disabledTooltip}>{label}</AvTooltip>;
          }
          return label;
        }}
      />
    </Box>
  ) : (
    <Box sx={{ ...flex.col, gap: 0.5 }}>
      <Box sx={{ fontSize: '12px', color: theme => theme.palette.colors.neutrals[550] }}>Break down by</Box>
      <Select
        options={SortedTimeBucketOptions}
        value={newWidget.requests[0].select.dims.find(({ name }) => hasTimeBucket(name))?.name}
        size="xSmall"
        getValueFunc={({ value }: any) => value}
        getLabelFunc={({ label }: any) => label}
        onChange={singleOnChange}
        isRequired
      />
    </Box>
  );
};

export default TimeBucketBreakdown;
